import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";

// ARCH-03 Shape B: public vendor self-registration form.
// No auth required. Posts to /api/t2t/vendor-portal/self-invite.
// Creates a pending row; admin must approve before the vendor gets a portal link.
//
// BUG-13: form expanded from 5 to 14 fields. Sensitive data (PAN, bank, KYC docs)
// stays in stage 2 (token-gated portal). This stage captures address + business
// fingerprint so admin has enough info to pre-screen.

const ACCOUNT_GROUPS = [
  { value: "goods",      label: "Goods" },
  { value: "services",   label: "Services" },
  { value: "contractor", label: "Contractor" },
  { value: "other",      label: "Other" },
];

// 28 Indian states + 8 union territories (post-2019). Sorted alphabetically.
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  // UTs:
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

// BUG-49: countries now load from /api/t2t/public/country (rate-limited public).
// The hardcoded list was removed; FALLBACK_COUNTRIES is kept only in case the
// API is unreachable on first load — admin sees a usable form even if backend
// is down. Sign-up form stores both country name AND iso2 code.
const FALLBACK_COUNTRIES = [
  { iso2: "IN", name: "India" },
  { iso2: "US", name: "United States" },
  { iso2: "GB", name: "United Kingdom" },
];

const TURNOVER_BRACKETS = [
  { value: "",                label: "Prefer not to say" },
  { value: "lt_1cr",          label: "Less than ₹1 Crore" },
  { value: "1cr_10cr",        label: "₹1 – 10 Crore" },
  { value: "10cr_50cr",       label: "₹10 – 50 Crore" },
  { value: "50cr_250cr",      label: "₹50 – 250 Crore" },
  { value: "gt_250cr",        label: "More than ₹250 Crore" },
];

const EMPTY_FORM = {
  vendor_name:             "",
  contact_name:            "",
  contact_email:           "",
  contact_mobile:          "",
  account_group:           "goods",
  // BUG-13 new fields
  address_line1:           "",
  city:                    "",
  state:                   "",
  country:                 "India",
  country_code:            "IN",   // BUG-49 — ISO-2 paired with country name
  pincode:                 "",
  website:                 "",
  gst_number:              "",
  business_description:    "",
  annual_turnover_bracket: "",
};

const T2TVendorSignup = () => {
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [submitting,setSubmitting]= useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState(false);
  // BUG-48: pincode/state mismatch banner. Empty when match or non-India.
  const [pinStateWarning, setPinStateWarning] = useState(null);
  // BUG-49: country list loaded from /api/t2t/public/country.
  // Falls back to FALLBACK_COUNTRIES if the API is unreachable on first load.
  const [countriesList, setCountriesList] = useState(FALLBACK_COUNTRIES);

  // Use a bare axios call — the default axiosInstance attaches a JWT and redirects
  // on 401, which is wrong for a public signup page where the visitor has no token.
  // baseURL is expected to already include the "/api" prefix (matches axiosInstance
  // convention). Fallback "/api" works with the CRA dev proxy when no env var is set.
  const baseURL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "/api";

  // BUG-48 — pincode auto-fill: on blur of a 6-digit pincode, fetch
  // state/district from the public lookup endpoint and always overwrite
  // State / City. India-only.
  const onPincodeBlur = async () => {
    const code = (form.pincode || "").trim();
    if (!/^\d{6}$/.test(code)) return;
    if (form.country !== "India") return;
    try {
      const resp = await axios.get(`${baseURL}/t2t/public/pincode/${encodeURIComponent(code)}`);
      const row = resp.data?.data;
      if (!row) return;
      // Map India Post state → INDIAN_STATES canonical option (case-insensitive)
      const matched = INDIAN_STATES.find((s) => s.toLowerCase() === (row.state || "").toLowerCase().trim()) || row.state;
      setForm((prev) => ({
        ...prev,
        state: matched || prev.state,
        city:  row.district || prev.city,
      }));
    } catch (_e) {
      // Silent — autofill is a courtesy. The submit-time backend check still enforces match.
    }
  };

  // BUG-49 — load countries once from the public lookup endpoint.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${baseURL}/t2t/public/country`)
      .then((resp) => {
        if (cancelled) return;
        const list = resp.data?.data;
        if (Array.isArray(list) && list.length > 0) setCountriesList(list);
      })
      .catch(() => { /* keep FALLBACK_COUNTRIES on failure */ });
    return () => { cancelled = true; };
  }, [baseURL]);

  // BUG-48 — live mismatch banner: fires when pincode + state both filled and
  // diverge. Hard backend block happens on submit; this is the early warning.
  useEffect(() => {
    const pin   = (form.pincode || "").trim();
    const state = (form.state    || "").trim();
    if (form.country !== "India" || !/^\d{6}$/.test(pin) || !state) {
      setPinStateWarning(null);
      return;
    }
    let cancelled = false;
    axios.get(`${baseURL}/t2t/public/pincode/${encodeURIComponent(pin)}`)
      .then((resp) => {
        if (cancelled) return;
        const row = resp.data?.data;
        if (!row) { setPinStateWarning(null); return; }
        const expected = (row.state || "").toLowerCase().trim();
        const supplied = state.toLowerCase();
        setPinStateWarning(expected === supplied
          ? null
          : { pincode: row.pincode, expected_state: row.state, supplied_state: state });
      })
      .catch(() => { if (!cancelled) setPinStateWarning(null); });
    return () => { cancelled = true; };
  }, [form.pincode, form.state, form.country, baseURL]);

  const submit = async () => {
    setError("");
    // ── Required field checks ──
    if (!form.vendor_name.trim())          { setError("Vendor / Company Name is required."); return; }
    if (!form.contact_name.trim())         { setError("Primary Contact Name is required."); return; }
    if (!form.contact_email.trim())        { setError("Contact Email is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
      setError("Please enter a valid email address."); return;
    }
    // BUG-50 — per-country phone-length validation. contact_mobile stores the
    // national-significant-number only (digits, no prefix). The "+91" prefix
    // is rendered visually but not part of the stored value.
    if (form.contact_mobile) {
      const digits = form.contact_mobile.replace(/\D/g, "");
      const ctry = countriesList.find((c) => c.name === form.country);
      const minLen = ctry?.phone_min_length || 7;
      const maxLen = ctry?.phone_max_length || 15;
      if (digits.length < minLen || digits.length > maxLen) {
        const range = minLen === maxLen ? `exactly ${minLen} digits` : `${minLen}–${maxLen} digits`;
        setError(`Contact Mobile must be ${range} for ${form.country || "the selected country"}.`);
        return;
      }
    }
    if (!form.address_line1.trim())        { setError("Address Line 1 is required."); return; }
    if (!form.city.trim())                 { setError("City is required."); return; }
    if (!form.state)                       { setError("State is required."); return; }
    if (!form.country)                     { setError("Country is required."); return; }
    if (!form.pincode.trim())              { setError("Pincode is required."); return; }
    // Pincode: 6 digits for India, looser for others (3-10 alphanumeric)
    if (form.country === "India" && !/^\d{6}$/.test(form.pincode.trim())) {
      setError("Pincode must be 6 digits for India."); return;
    }
    if (form.country !== "India" && !/^[A-Za-z0-9 -]{3,10}$/.test(form.pincode.trim())) {
      setError("Please enter a valid pincode / postal code."); return;
    }
    // GST optional — but if filled, validate Indian GSTIN format (15 chars: 2 digits state + 10 PAN + 1 entity + 1 Z + 1 check)
    if (form.gst_number) {
      const gst = form.gst_number.trim().toUpperCase();
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst)) {
        setError("Please enter a valid 15-character GSTIN, or leave it blank."); return;
      }
    }
    // Website optional — validate URL shape if provided
    if (form.website) {
      const w = form.website.trim();
      if (!/^(https?:\/\/)?[\w.-]+\.[a-zA-Z]{2,}([/?#].*)?$/.test(w)) {
        setError("Please enter a valid website URL, or leave it blank."); return;
      }
    }
    if (!form.business_description.trim()) {
      setError("Brief description of services is required."); return;
    }
    if (form.business_description.trim().length < 30) {
      setError("Business description must be at least 30 characters."); return;
    }
    if (form.business_description.length > 2000) {
      setError("Business description must be at most 2000 characters."); return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        gst_number: form.gst_number ? form.gst_number.trim().toUpperCase() : "",
        // strip leading + on pincode etc. just in case
      };
      const resp = await axios.post(`${baseURL}/t2t/vendor-portal/self-invite`, payload);
      if (resp.data?.status === 201 || resp.status === 201) {
        setSuccess(true);
      } else {
        setError(resp.data?.message || "Submission failed. Please try again.");
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Submission failed. Please try again.";
      if (e.response?.status === 429) {
        setError("Too many sign-up attempts from this network. Please try again later.");
      } else {
        setError(msg);
      }
    }
    setSubmitting(false);
  };

  // ── Success state ──
  if (success) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 42, marginBottom: 12 }}>✓</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 10px", color: "#0F172A" }}>Thank you</h1>
          <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.6, margin: "0 0 10px" }}>
            Your request has been received. A team member will review it and email you a portal link once approved.
          </p>
          <p style={{ fontSize: 12, color: "#94A3B8", margin: 0 }}>
            You can close this tab. No further action is needed right now.
          </p>
        </div>
      </div>
    );
  }

  // ── Form state ──
  const stateOptions = form.country === "India" ? INDIAN_STATES : null;
  const charCount = form.business_description.length;

  return (
    <div style={pageStyle}>
      <div style={{ ...cardStyle, maxWidth: 640 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 6px", color: "#0F172A" }}>Vendor Registration</h1>
        <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 20px", lineHeight: 1.6 }}>
          Register your organisation to be considered as a supplier. Our team will review your request and
          email you a secure link to complete the full vendor onboarding form (PAN, GST documents, bank details, etc.).
        </p>

        {error && (
          <div style={{ padding: "10px 12px", background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA",
            borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        {/* Section: Company */}
        <SectionHeader>Company</SectionHeader>

        <Field label="Vendor / Company Name *" value={form.vendor_name}
          onChange={(v) => setForm({ ...form, vendor_name: v })} />

        <div style={twoColStyle}>
          <Field label="Vendor Type *" type="select" value={form.account_group}
            options={ACCOUNT_GROUPS}
            onChange={(v) => setForm({ ...form, account_group: v })} />
          <Field label="Annual Turnover" type="select" value={form.annual_turnover_bracket}
            options={TURNOVER_BRACKETS}
            onChange={(v) => setForm({ ...form, annual_turnover_bracket: v })} />
        </div>

        <div style={twoColStyle}>
          <Field label="Website" type="url" value={form.website} placeholder="https://example.com"
            onChange={(v) => setForm({ ...form, website: v })} />
          <Field label="GST Number" value={form.gst_number} placeholder="15-character GSTIN"
            onChange={(v) => setForm({ ...form, gst_number: v.toUpperCase() })} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>
            Brief description of services *
            <span style={{ float: "right", fontSize: 11, fontWeight: 400, color: charCount > 2000 ? "#B91C1C" : "#94A3B8" }}>
              {charCount} / 2000
            </span>
          </label>
          <textarea
            style={{ ...inputStyle, minHeight: 80, resize: "vertical", fontFamily: "inherit" }}
            value={form.business_description}
            onChange={(e) => setForm({ ...form, business_description: e.target.value })}
            placeholder="What does your company do? (30 – 2000 characters)" />
        </div>

        {/* Section: Contact */}
        <SectionHeader>Primary Contact</SectionHeader>

        <Field label="Primary Contact Name *" value={form.contact_name}
          onChange={(v) => setForm({ ...form, contact_name: v })} />
        <div style={twoColStyle}>
          <Field label="Contact Email *" type="email" value={form.contact_email}
            onChange={(v) => setForm({ ...form, contact_email: v })} />
          {/* BUG-50 — Contact Mobile with country-aware "+code" prefix badge + length validation */}
          {(() => {
            const ctry = countriesList.find((c) => c.name === form.country);
            const phoneCode = ctry?.phone_code || "";
            const minLen = ctry?.phone_min_length || 7;
            const maxLen = ctry?.phone_max_length || 15;
            const digits = (form.contact_mobile || "").replace(/\D/g, "");
            const inRange = digits.length === 0 || (digits.length >= minLen && digits.length <= maxLen);
            const hint = minLen === maxLen ? `Exactly ${minLen} digits` : `${minLen}–${maxLen} digits`;
            return (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Contact Mobile</label>
                <div style={{ display: "flex", alignItems: "stretch" }}>
                  {phoneCode && (
                    <span style={{
                      display: "inline-flex", alignItems: "center",
                      padding: "0 10px", fontSize: 13, color: "#334155",
                      background: "#F1F5F9", border: "1px solid #CBD5E1",
                      borderRight: "none",
                      borderTopLeftRadius: 6, borderBottomLeftRadius: 6,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}>+{phoneCode}</span>
                  )}
                  <input
                    type="tel"
                    style={{
                      ...inputStyle,
                      ...(phoneCode ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {}),
                      flex: 1,
                    }}
                    value={form.contact_mobile}
                    placeholder={minLen === maxLen ? `${"X".repeat(minLen)} (${minLen} digits)` : `${minLen}–${maxLen} digits`}
                    maxLength={maxLen + 4}
                    onChange={(e) => setForm({ ...form, contact_mobile: e.target.value.replace(/[^\d\s-]/g, "") })}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginTop: 3 }}>
                  <span style={{ color: "#94A3B8" }}>{hint}</span>
                  <span style={{ color: inRange ? "#16A34A" : "#DC2626", fontWeight: 600 }}>
                    {digits.length}/{maxLen}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Section: Address */}
        <SectionHeader>Registered Address</SectionHeader>

        <Field label="Address Line 1 *" value={form.address_line1}
          onChange={(v) => setForm({ ...form, address_line1: v })} />

        <div style={twoColStyle}>
          <Field label="City *" value={form.city}
            onChange={(v) => setForm({ ...form, city: v })} />
          <Field label="Country *" type="select" value={form.country}
            options={countriesList.map((c) => ({ value: c.name, label: c.name }))}
            onChange={(name) => {
              // BUG-49: country pick sets both display name AND iso2 code; clears state
              // since state options depend on country (India has dropdown, others free text).
              const match = countriesList.find((c) => c.name === name);
              setForm({
                ...form,
                country: name,
                country_code: match?.iso2 || "",
                state: "",
              });
            }} />
        </div>

        <div style={twoColStyle}>
          {stateOptions ? (
            <Field label="State *" type="select" value={form.state}
              options={[{ value: "", label: "Select state..." }, ...stateOptions.map(s => ({ value: s, label: s }))]}
              onChange={(v) => setForm({ ...form, state: v })} />
          ) : (
            <Field label="State / Province *" value={form.state}
              onChange={(v) => setForm({ ...form, state: v })} />
          )}
          <Field label="Pincode *" value={form.pincode} placeholder={form.country === "India" ? "6 digits" : "Postal code"}
            onChange={(v) => setForm({ ...form, pincode: v })}
            onBlur={onPincodeBlur} />
        </div>

        {/* BUG-48 — live pincode/state mismatch banner */}
        {pinStateWarning && (
          <div style={{
            padding: "10px 12px",
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderRadius: 8,
            fontSize: 12,
            color: "#92400E",
            marginBottom: 14,
            lineHeight: 1.5,
          }}>
            <strong>⚠ Pincode / State mismatch:</strong> Pincode <code style={{ background: "#FEF3C7", padding: "1px 6px", borderRadius: 3 }}>{pinStateWarning.pincode}</code> belongs to <strong>{pinStateWarning.expected_state}</strong>, but the State you selected is <strong>{pinStateWarning.supplied_state}</strong>. Please correct one before submitting.
          </div>
        )}

        <button
          onClick={submit}
          disabled={submitting}
          style={{
            width: "100%", padding: "12px 16px", fontSize: 14, fontWeight: 700,
            background: submitting ? "#94A3B8" : "#16A34A", color: "white",
            border: "none", borderRadius: 8, cursor: submitting ? "wait" : "pointer",
            marginTop: 8,
          }}
        >
          {submitting ? "Submitting..." : "Submit Registration"}
        </button>

        <p style={{ fontSize: 11, color: "#94A3B8", margin: "14px 0 0", textAlign: "center" }}>
          By submitting, you acknowledge that approval is subject to our internal review process.
          Sensitive details (PAN, bank account, KYC documents) are collected only after approval, on a secure portal link sent to your email.
        </p>
      </div>
    </div>
  );
};

// ── Inline style helpers ──
const pageStyle = {
  minHeight: "100vh",
  background: "linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};
const cardStyle = {
  width: "100%", maxWidth: 480,
  background: "white", borderRadius: 12, padding: "32px 32px 28px",
  boxShadow: "0 10px 40px rgba(15, 76, 129, 0.12)",
  border: "1px solid #E2E8F0",
};
const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#334155",
  marginBottom: 6,
};
const inputStyle = {
  width: "100%", padding: "9px 12px", fontSize: 13,
  border: "1px solid #CBD5E1", borderRadius: 6,
  background: "white", color: "#0F172A",
  boxSizing: "border-box",
};
const twoColStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const SectionHeader = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
    color: "#64748B", textTransform: "uppercase",
    marginBottom: 8, marginTop: 6,
    paddingBottom: 4, borderBottom: "1px solid #E2E8F0",
  }}>
    {children}
  </div>
);
SectionHeader.propTypes = { children: PropTypes.node.isRequired };

const Field = ({ label, value, onChange, onBlur, type = "text", options = null, placeholder = "" }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={labelStyle}>{label}</label>
    {type === "select" && options ? (
      <select style={inputStyle} value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <input type={type} style={inputStyle} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
    )}
  </div>
);
Field.propTypes = {
  label:       PropTypes.string.isRequired,
  value:       PropTypes.string.isRequired,
  onChange:    PropTypes.func.isRequired,
  onBlur:      PropTypes.func,
  type:        PropTypes.string,
  options:     PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })),
  placeholder: PropTypes.string,
};

export default T2TVendorSignup;
